type Country = 'cn' | 'kr';
type ChemicalEnv = 'qa' | 'prod';

type PublicEnv = {
  COUNTRY: Country;
  CHEMICAL_ENV: ChemicalEnv;
  DOMAIN: string;
  CURRENCY: string;
  COA_ROOT_DOMAIN: string;
  PSYS_LINK?: string;
};

const PSYS_LINK_BY_ENV: Record<ChemicalEnv, string> = {
  qa: 'https://sandbox.psys.co.kr/outvendnew/vendor/input',
  prod: 'https://www.psys.co.kr/outvendnew/vendor/input.php',
};

const COA_ROOT_DOMAIN_BY_ENV: Record<ChemicalEnv, string> = {
  qa: 'https://assets.qa.thermofisher.com/',
  prod: 'https://assets.thermofisher.com/',
};

function deriveEnv(country: Country, chemicalEnv: ChemicalEnv): PublicEnv {
  const domainPrefix = chemicalEnv === 'qa' ? 'qa1-' : '';
  return {
    COUNTRY: country,
    CHEMICAL_ENV: chemicalEnv,
    DOMAIN: `https://${domainPrefix}chemicals.thermofisher.${country}`,
    CURRENCY: country === 'cn' ? 'CNY' : 'KRW',
    COA_ROOT_DOMAIN: COA_ROOT_DOMAIN_BY_ENV[chemicalEnv],
    PSYS_LINK: country === 'kr' ? PSYS_LINK_BY_ENV[chemicalEnv] : undefined,
  };
}

// COUNTRY is the only value that needs injection — same image serves CN and KR,
// hostname alone can't distinguish (custom alias domains may not encode country).
// CHEMICAL_ENV is fully derivable from hostname: qa1- prefix means QA, no prefix means PROD.
function readCountry(): Country {
  if (typeof window !== 'undefined') {
    return (document.documentElement.dataset.country ?? 'cn') as Country;
  }
  return (process.env.COUNTRY ?? 'cn') as Country;
}

function readChemicalEnv(): ChemicalEnv {
  if (typeof window !== 'undefined') {
    // Local `next dev` runs on localhost where hostname can't tell us anything;
    // NODE_ENV is inlined by Next.js so this collapses to a constant in prod bundles.
    if (process.env.NODE_ENV === 'development') return 'qa';
    return window.location.hostname.startsWith('qa1-') ? 'qa' : 'prod';
  }
  return (process.env.CHEMICAL_ENV ?? 'prod') as ChemicalEnv;
}

export const env: PublicEnv = deriveEnv(readCountry(), readChemicalEnv());
